import { Http } from '../http';
import type {
  AdminWorkspaceInfo,
  AdminWorkspacesResponse,
  AntiochWorkspace,
  AssetListResponse,
  CurrentUserResponse,
  DeleteScenariosResponse,
  DownloadUrlsResponse,
  GetScenarioRunResponse,
  GpuStatusResponse,
  InviteMemberResponse,
  MintTokensResponse,
  OrgMembersResponse,
  OrgRole,
  PendingInvitesResponse,
  QueryScenariosRequest,
  QueryScenariosResponse,
  QuerySuitesResponse,
  RemoveMemberResponse,
  RevokeInviteResponse,
  RomeWorkspace,
  RomeWorkspaceStartResponse,
  RomeWorkspacesResponse,
  ScenarioSuggestContext,
  ScenarioSuggestResponse,
  ServiceTokens,
  SSHKeyResponse,
  SSHKeysListResponse,
  UpdateRoleResponse,
  UsageResponse,
  WorkspaceAuthStartResponse,
  WorkspaceImagesResponse,
  WorkspaceLimitsResponse,
  WorkspaceProgressResponse,
  WorkspaceState,
  WorkspaceVersionsResponse,
} from '../types/rome';
import { isLocalHost } from '../utils/workspace';

// Helper function to build URLs (same pattern as simulation API)
function buildUrl(path: string): string {
  const hostname = typeof window !== 'undefined' ? (window.location?.hostname ?? '') : '';
  if (isLocalHost(hostname)) {
    // In development, use the proxy path to avoid CORS issues
    return `/api${path}`;
  }

  // In production, use the full URL from environment variable
  const ROME_API_URL = import.meta.env.VITE_ROME_API_URL || 'http://localhost:8080';
  return `${ROME_API_URL}${path}`;
}

/**
 * Fetches user workspaces from the Rome API.
 *
 * @param accessToken - The Auth0 access token.
 * @returns Promise with the workspaces response.
 */
export async function getUserWorkspaces(
  accessToken: string
): Promise<{ status: number; data: RomeWorkspacesResponse | null }> {
  const url = buildUrl('/workspace/user');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<RomeWorkspacesResponse>(url, headers);
}

/**
 * Fetches startup progress for a workspace from the Rome API
 * @param accessToken - The Auth0 access token
 * @param workspaceId - The workspace ID to get progress for
 * @returns Promise with the workspace progress response
 */
export async function getWorkspaceProgress(
  accessToken: string,
  workspaceId: string
): Promise<{ status: number; data: WorkspaceProgressResponse | null }> {
  const url = buildUrl(`/workspace/progress?workspace_id=${encodeURIComponent(workspaceId)}`);
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<WorkspaceProgressResponse>(url, headers);
}

/**
 * Fetches available workspace versions from the Rome API
 * @param accessToken - The Auth0 access token
 * @returns Promise with the available versions response
 */
export async function getWorkspaceVersions(
  accessToken: string
): Promise<{ status: number; data: WorkspaceVersionsResponse | null }> {
  const url = buildUrl('/workspace/versions');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<WorkspaceVersionsResponse>(url, headers);
}

/**
 * Fetches the workspace image catalog (central antioch-sim versions plus the
 * org's custom images) from the Rome API
 * @param accessToken - The Auth0 access token
 * @returns Promise with the available images response
 */
export async function getWorkspaceImages(
  accessToken: string,
  revealNames = false
): Promise<{ status: number; data: WorkspaceImagesResponse | null }> {
  // reveal_names asks Rome to resolve friendly customer names for images the
  // caller does not own. Rome honors it only for Antioch-staff callers and
  // ignores it otherwise, so sending it from a non-staff session is harmless.
  const url = buildUrl(revealNames ? '/workspace/images?reveal_names=true' : '/workspace/images');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<WorkspaceImagesResponse>(url, headers);
}

/**
 * Starts a new workspace for the user
 * @param accessToken - The Auth0 access token
 * @param options - Start options
 * @returns Promise with the started workspace response
 */
export async function startWorkspace(
  accessToken: string,
  options?: {
    workspaceName?: string;
    version?: string;
    image?: string;
    useLatestImages?: boolean;
    workspaceAuthDeviceCode?: string;
    workspaceAuthInterval?: number;
  }
): Promise<{ status: number; data: RomeWorkspaceStartResponse | null }> {
  const url = buildUrl('/workspace/start');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  // workspace_auth_device_code lets Rome exchange Auth0's device flow into a
  // native-client refresh token only once the user approves it.
  const body: {
    name?: string;
    version?: string;
    image?: string;
    use_latest_images?: boolean;
    workspace_auth_device_code?: string;
    workspace_auth_interval?: number;
  } = {};
  if (options?.workspaceName) body.name = options.workspaceName;
  if (options?.version) body.version = options.version;
  if (options?.image) body.image = options.image;
  if (options?.useLatestImages) body.use_latest_images = options.useLatestImages;
  if (options?.workspaceAuthDeviceCode) body.workspace_auth_device_code = options.workspaceAuthDeviceCode;
  if (options?.workspaceAuthInterval) body.workspace_auth_interval = options.workspaceAuthInterval;

  return Http.post<typeof body, RomeWorkspaceStartResponse>(url, body, headers);
}

export async function startWorkspaceAuth(
  accessToken: string
): Promise<{ status: number; data: WorkspaceAuthStartResponse | null }> {
  const url = buildUrl('/workspace/auth/start');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<Record<string, never>, WorkspaceAuthStartResponse>(url, {}, headers);
}

/**
 * Transforms Rome workspace data to UI-friendly format
 * @param romeWorkspaces - Array of workspaces from Rome API
 * @returns Array of transformed workspaces for UI consumption
 */
export function transformWorkspacesForUI(romeWorkspaces: RomeWorkspace[]): AntiochWorkspace[] {
  return romeWorkspaces.map((workspace) => ({
    id: workspace.workspace_id,
    name: workspace.name || workspace.workspace_id,
    hostname: workspace.hostname,
    ipAddress: workspace.ip_address,
    createdAt: workspace.created_at || workspace.starting_at || 0,
    runningAt: workspace.running_at,
    stoppedAt: workspace.stopped_at,
    erroredAt: workspace.errored_at,
    version: workspace.version,
    image: workspace.image || undefined,
    failureReason: workspace.failure_reason,
    scheduledShutdownAt: workspace.scheduled_shutdown_at || undefined,
    state: workspace.state as WorkspaceState,
  }));
}

/**
 * Fetches all workspaces (admin) from the Rome API.
 *
 * When ``states`` is provided, the backend only reads Redis sets for those
 * states, skipping expensive reads and Auth0 lookups for excluded states.
 *
 * @param accessToken - The Auth0 access token.
 * @param states - Optional array of states to fetch. If omitted, all states are returned.
 * @returns Promise with the workspaces response.
 */
export async function getAllWorkspaces(
  accessToken: string,
  states?: string[]
): Promise<{ status: number; data: AdminWorkspacesResponse | null }> {
  let url = buildUrl('/workspace/all');
  if (states?.length) {
    const searchParams = new URLSearchParams({ states: states.join(',') });
    url = `${url}?${searchParams.toString()}`;
  }
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<AdminWorkspacesResponse>(url, headers);
}

/**
 * Fetches the live GPU/cluster-capacity snapshot for the admin panel.
 *
 * Admin-only. Returns a server-side snapshot (refreshed on a background
 * interval in Rome), so it is cheap to poll frequently.
 *
 * @param accessToken - The Auth0 access token.
 * @returns Promise with the GPU status response.
 */
export async function getGpuStatus(accessToken: string): Promise<{ status: number; data: GpuStatusResponse | null }> {
  const url = buildUrl('/admin/gpu-status');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<GpuStatusResponse>(url, headers);
}

/**
 * Transforms admin workspace info to UI-friendly format
 * @param workspaceInfo - The workspace info from admin API
 * @returns Transformed workspace for UI consumption
 */
export function transformAdminWorkspaceForUI(workspaceInfo: AdminWorkspaceInfo): AntiochWorkspace {
  return {
    id: workspaceInfo.workspace_id,
    name: workspaceInfo.name || workspaceInfo.workspace_id,
    hostname: workspaceInfo.hostname,
    ipAddress: workspaceInfo.ip_address,
    createdAt: workspaceInfo.created_at || workspaceInfo.starting_at || 0,
    runningAt: workspaceInfo.running_at,
    stoppedAt: workspaceInfo.stopped_at,
    version: workspaceInfo.version,
    state: workspaceInfo.state as WorkspaceState,
  };
}

/**
 * Stops a workspace for the user
 * @param accessToken - The Auth0 access token
 * @param workspaceId - The workspace ID to stop
 * @returns Promise with the stop response
 */
export async function stopWorkspace(
  accessToken: string,
  workspaceId: string
): Promise<{ status: number; data: { message: string; status_code: number } | null }> {
  const url = buildUrl('/workspace/stop');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<{ workspace_id: string }, { message: string; status_code: number }>(
    url,
    { workspace_id: workspaceId },
    headers
  );
}

/**
 * Restarts a stopped workspace
 * @param accessToken - The Auth0 access token
 * @param workspaceId - The workspace ID to restart
 * @returns Promise with the restarted workspace response
 */
export async function restartWorkspace(
  accessToken: string,
  workspaceId: string,
  version?: string,
  image?: string,
  workspaceAuthDeviceCode?: string,
  workspaceAuthInterval?: number
): Promise<{ status: number; data: RomeWorkspaceStartResponse | null }> {
  const url = buildUrl('/workspace/restart');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  const body: {
    workspace_id: string;
    version?: string;
    image?: string;
    workspace_auth_device_code?: string;
    workspace_auth_interval?: number;
  } = {
    workspace_id: workspaceId,
  };
  if (version) body.version = version;
  if (image) body.image = image;
  if (workspaceAuthDeviceCode) body.workspace_auth_device_code = workspaceAuthDeviceCode;
  if (workspaceAuthInterval) body.workspace_auth_interval = workspaceAuthInterval;

  return Http.post<typeof body, RomeWorkspaceStartResponse>(url, body, headers);
}

/**
 * Terminates a workspace completely (deletes all resources)
 * @param accessToken - The Auth0 access token
 * @param workspaceId - The workspace ID to terminate
 * @returns Promise with the terminate response
 */
export async function terminateWorkspace(
  accessToken: string,
  workspaceId: string
): Promise<{ status: number; data: { message: string; status_code: number } | null }> {
  const url = buildUrl('/workspace/terminate');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.delete<{ workspace_id: string }, { message: string; status_code: number }>(
    url,
    { workspace_id: workspaceId },
    headers
  );
}

/**
 * Fetches the list of available assets from the Rome API
 * @param accessToken - The Auth0 access token
 * @returns Promise with the asset list response
 */
export async function listAssets(
  accessToken: string,
  folder?: string
): Promise<{ status: number; data: AssetListResponse | null }> {
  const params = new URLSearchParams();
  if (folder) params.set('folder', folder);
  const query = params.toString();
  const url = buildUrl('/asset/list') + (query ? `?${query}` : '');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<AssetListResponse>(url, headers);
}

interface PushAssetResponse {
  upload_url: string;
  required_headers: Record<string, string>;
  message: string;
  status_code: number;
}

/**
 * Requests a signed upload URL for an asset file from Rome, then uploads the
 * file directly to GCS.
 */
export async function pushAsset(
  accessToken: string,
  params: {
    name: string;
    version: string;
    extension: string;
    file_size: number;
    modified_time: string;
    file: File;
  }
): Promise<{ status: number; error?: string }> {
  // Get a signed upload URL from Rome
  const query = new URLSearchParams({
    name: params.name,
    version: params.version,
    extension: params.extension,
    file_size: params.file_size.toString(),
    modified_time: params.modified_time,
  });
  const url = `${buildUrl('/asset/push')}?${query}`;
  const romeRes = await Http.post<Record<string, never>, PushAssetResponse>(
    url,
    {},
    {
      Authorization: `Bearer ${accessToken}`,
    }
  );

  if (romeRes.status !== 200 || !romeRes.data) {
    const msg = (romeRes.data as { message?: string } | null)?.message ?? 'Failed to get upload URL';
    return { status: romeRes.status, error: msg };
  }

  // Upload file bytes directly to GCS via the signed URL
  const gcsRes = await fetch(romeRes.data.upload_url, {
    method: 'PUT',
    headers: {
      ...romeRes.data.required_headers,
      'Content-Type': 'application/octet-stream',
    },
    body: params.file,
  });

  if (!gcsRes.ok) {
    return { status: gcsRes.status, error: `GCS upload failed (${gcsRes.status})` };
  }

  return { status: 200 };
}

/**
 * Push an asset with upload progress tracking via XMLHttpRequest.
 *
 * First fetches a signed upload URL from Rome, then uploads the file directly
 * to GCS. The returned promise resolves when the GCS upload completes (or
 * rejects on network error). Use the onProgress callback to track upload bytes.
 */
export async function pushAssetWithProgress(
  accessToken: string,
  params: {
    name: string;
    version: string;
    extension: string;
    file_size: number;
    modified_time: string;
    file: File;
  },
  onProgress: (loaded: number, total: number) => void
): Promise<{ status: number; error?: string }> {
  const query = new URLSearchParams({
    name: params.name,
    version: params.version,
    extension: params.extension,
    file_size: params.file_size.toString(),
    modified_time: params.modified_time,
  });
  const url = `${buildUrl('/asset/push')}?${query}`;
  const romeRes = await Http.post<Record<string, never>, PushAssetResponse>(
    url,
    {},
    {
      Authorization: `Bearer ${accessToken}`,
    }
  );

  if (romeRes.status !== 200 || !romeRes.data) {
    const msg = (romeRes.data as { message?: string } | null)?.message ?? 'Failed to get upload URL';
    return { status: romeRes.status, error: msg };
  }

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', romeRes.data!.upload_url);

    for (const [key, value] of Object.entries(romeRes.data!.required_headers)) {
      xhr.setRequestHeader(key, value);
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: 200 });
      } else {
        resolve({ status: xhr.status, error: `GCS upload failed (${xhr.status})` });
      }
    };

    xhr.onerror = () => {
      resolve({ status: 0, error: 'Upload failed -- network error' });
    };

    xhr.send(params.file);
  });
}

/**
 * Uploads a preview image for an existing asset version.
 */
export async function pushAssetPreview(
  accessToken: string,
  params: {
    name: string;
    version: string;
    extension: string;
    file: File;
  }
): Promise<{ status: number; error?: string }> {
  const query = new URLSearchParams({
    name: params.name,
    version: params.version,
    extension: params.extension,
  });
  const url = `${buildUrl('/asset/push/preview')}?${query}`;
  const romeRes = await Http.post<Record<string, never>, PushAssetResponse>(
    url,
    {},
    {
      Authorization: `Bearer ${accessToken}`,
    }
  );

  if (romeRes.status !== 200 || !romeRes.data) {
    const msg = (romeRes.data as { message?: string } | null)?.message ?? 'Failed to get preview upload URL';
    return { status: romeRes.status, error: msg };
  }

  const contentType = params.extension === 'png' ? 'image/png' : 'image/jpeg';
  const gcsRes = await fetch(romeRes.data.upload_url, {
    method: 'PUT',
    headers: {
      ...romeRes.data.required_headers,
      'Content-Type': contentType,
    },
    body: params.file,
  });

  if (!gcsRes.ok) {
    return { status: gcsRes.status, error: `GCS preview upload failed (${gcsRes.status})` };
  }

  return { status: 200 };
}

interface MoveAssetResponse {
  old_name: string;
  new_name: string;
  message: string;
  status_code: number;
}

/**
 * Move or rename an asset. Copies blobs to the new path and records an alias
 * so old references continue to resolve.
 */
export async function moveAsset(
  accessToken: string,
  name: string,
  newName: string,
  force: boolean = false
): Promise<{ status: number; data: MoveAssetResponse | null }> {
  const url = buildUrl('/asset/move');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<{ name: string; new_name: string; force: boolean }, MoveAssetResponse>(
    url,
    { name, new_name: newName, force },
    headers
  );
}

interface DeleteAssetResponse {
  deleted_count: number;
  message: string;
  status_code: number;
}

/**
 * Delete an asset. Requires confirm=true. Optionally delete a single version.
 */
export async function deleteAsset(
  accessToken: string,
  name: string,
  version?: string
): Promise<{ status: number; data: DeleteAssetResponse | null }> {
  const url = buildUrl('/asset/delete');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  const body: { name: string; confirm: boolean; version?: string } = { name, confirm: true };
  if (version) body.version = version;

  return Http.post<typeof body, DeleteAssetResponse>(url, body, headers);
}

interface ListAliasesResponse {
  aliases: Record<string, string>;
  message: string;
  status_code: number;
}

/**
 * List all asset aliases for the organization. Admin-only.
 */
export async function listAliases(accessToken: string): Promise<{ status: number; data: ListAliasesResponse | null }> {
  const url = buildUrl('/asset/aliases');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<ListAliasesResponse>(url, headers);
}

/**
 * Delete the preview image for an asset version. Admin-only.
 */
export async function deleteAssetPreview(
  accessToken: string,
  name: string,
  version: string
): Promise<{ status: number; data: { deleted: boolean; message: string } | null }> {
  const url = buildUrl('/asset/delete-preview');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<{ name: string; version: string }, { deleted: boolean; message: string; status_code: number }>(
    url,
    { name, version },
    headers
  );
}

/**
 * Query scenario runs by organization with optional filters
 * @param accessToken - The Auth0 access token
 * @param filters - Optional filter parameters
 * @returns Promise with the scenario runs query response
 */
export async function queryScenarios(
  accessToken: string,
  filters?: QueryScenariosRequest,
  options?: { signal?: AbortSignal }
): Promise<{ status: number; data: QueryScenariosResponse | null }> {
  const params = new URLSearchParams();

  if (filters?.date_from) params.append('date_from', filters.date_from);
  if (filters?.date_to) params.append('date_to', filters.date_to);
  if (filters?.limit !== undefined) params.append('limit', filters.limit.toString());
  if (filters?.offset !== undefined) params.append('offset', filters.offset.toString());
  if (filters?.cursor) params.append('cursor', filters.cursor);
  if (filters?.match_substrings !== undefined) params.append('match_substrings', filters.match_substrings.toString());
  if (filters?.outcome) params.append('outcome', filters.outcome);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.suite) params.append('suite', filters.suite);
  if (filters?.suite_run) params.append('suite_run', filters.suite_run);
  if (filters?.scenario_name) params.append('scenario_name', filters.scenario_name);
  if (filters?.tags) {
    for (const tag of filters.tags) {
      params.append('tags', tag);
    }
  }
  if (filters?.exclude_tags) {
    for (const tag of filters.exclude_tags) {
      params.append('exclude_tags', tag);
    }
  }
  if (filters?.search_terms) {
    for (const term of filters.search_terms) {
      params.append('search_terms', term);
    }
  }
  if (filters?.exclude_search_terms) {
    for (const term of filters.exclude_search_terms) {
      params.append('exclude_search_terms', term);
    }
  }
  if (filters?.tag_groups) {
    for (const group of filters.tag_groups) {
      params.append('tag_groups', group);
    }
  }
  if (filters?.name_term_groups) {
    for (const group of filters.name_term_groups) {
      params.append('name_term_groups', group);
    }
  }
  if (filters?.user_id) params.append('user_id', filters.user_id);
  if (filters?.user_ids) {
    for (const uid of filters.user_ids) {
      params.append('user_ids', uid);
    }
  }
  if (filters?.exclude_user_ids) {
    for (const uid of filters.exclude_user_ids) {
      params.append('exclude_user_ids', uid);
    }
  }
  if (filters?.exclude_scenario_names) {
    for (const name of filters.exclude_scenario_names) {
      params.append('exclude_scenario_names', name);
    }
  }

  const queryString = params.toString();
  const url = buildUrl(`/scenarios/query${queryString ? `?${queryString}` : ''}`);
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  // Custom filters go in the request body
  const body = filters?.custom_filters ? { custom_filters: filters.custom_filters } : {};

  return Http.post<typeof body, QueryScenariosResponse>(url, body, headers, options);
}

/**
 * Query suite summaries aggregated server-side from scenario runs.
 *
 * @param accessToken - The Auth0 access token.
 * @param filters - Optional filter parameters (search, date_from, date_to, tags, user_id, limit).
 * @returns Promise with suite summaries.
 */
export async function querySuites(
  accessToken: string,
  filters?: {
    search?: string;
    search_terms?: string[];
    exclude_search_terms?: string[];
    date_from?: string;
    date_to?: string;
    tags?: string[];
    exclude_tags?: string[];
    tag_groups?: string[];
    name_term_groups?: string[];
    user_id?: string;
    user_ids?: string[];
    exclude_user_ids?: string[];
    exclude_scenario_names?: string[];
    limit?: number;
  }
): Promise<{ status: number; data: QuerySuitesResponse | null }> {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.date_from) params.append('date_from', filters.date_from);
  if (filters?.date_to) params.append('date_to', filters.date_to);
  if (filters?.tags) {
    for (const tag of filters.tags) {
      params.append('tags', tag);
    }
  }
  if (filters?.exclude_tags) {
    for (const tag of filters.exclude_tags) {
      params.append('exclude_tags', tag);
    }
  }
  if (filters?.search_terms) {
    for (const term of filters.search_terms) {
      params.append('search_terms', term);
    }
  }
  if (filters?.exclude_search_terms) {
    for (const term of filters.exclude_search_terms) {
      params.append('exclude_search_terms', term);
    }
  }
  if (filters?.tag_groups) {
    for (const group of filters.tag_groups) {
      params.append('tag_groups', group);
    }
  }
  if (filters?.name_term_groups) {
    for (const group of filters.name_term_groups) {
      params.append('name_term_groups', group);
    }
  }
  if (filters?.user_id) params.append('user_id', filters.user_id);
  if (filters?.user_ids) {
    for (const uid of filters.user_ids) {
      params.append('user_ids', uid);
    }
  }
  if (filters?.exclude_user_ids) {
    for (const uid of filters.exclude_user_ids) {
      params.append('exclude_user_ids', uid);
    }
  }
  if (filters?.exclude_scenario_names) {
    for (const name of filters.exclude_scenario_names) {
      params.append('exclude_scenario_names', name);
    }
  }
  if (filters?.limit !== undefined) params.append('limit', filters.limit.toString());

  const queryString = params.toString();
  const url = buildUrl(`/scenarios/suites${queryString ? `?${queryString}` : ''}`);
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<QuerySuitesResponse>(url, headers);
}

/**
 * Get signed download URLs for scenario run files (MCAP and bundle)
 * @param accessToken - The Auth0 access token
 * @param runId - The run ID
 * @returns Promise with download URLs and filenames
 */
export async function getDownloadUrls(
  accessToken: string,
  runId: string
): Promise<{ status: number; data: DownloadUrlsResponse | null }> {
  const url = buildUrl(`/scenarios/runs/download-urls?run_id=${runId}`);
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<DownloadUrlsResponse>(url, headers);
}

/**
 * Fetch a single scenario run by ID.
 */
export async function getScenarioRun(
  accessToken: string,
  runId: string
): Promise<{ status: number; data: GetScenarioRunResponse | null }> {
  const url = buildUrl(`/scenarios/runs/info?run_id=${runId}`);
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<GetScenarioRunResponse>(url, headers);
}

/**
 * Delete a single scenario run and its GCS artifacts.
 */
export async function deleteScenarioRun(
  accessToken: string,
  runId: string
): Promise<{ status: number; data: DeleteScenariosResponse | null }> {
  const url = buildUrl(`/scenarios/runs?run_id=${encodeURIComponent(runId)}`);
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.delete<Record<string, never>, DeleteScenariosResponse>(url, {}, headers);
}

/**
 * Delete all scenario runs in a suite and their GCS artifacts.
 */
export async function deleteScenarioSuite(
  accessToken: string,
  suite: string
): Promise<{ status: number; data: DeleteScenariosResponse | null }> {
  const url = buildUrl(`/scenarios/suites?suite=${encodeURIComponent(suite)}`);
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.delete<Record<string, never>, DeleteScenariosResponse>(url, {}, headers);
}

/**
 * Delete all scenario runs in a specific suite run.
 */
export async function deleteScenarioSuiteRun(
  accessToken: string,
  suite: string,
  suiteRun: string
): Promise<{ status: number; data: DeleteScenariosResponse | null }> {
  const params = new URLSearchParams({ suite, suite_run: suiteRun });
  const url = buildUrl(`/scenarios/suite-runs?${params.toString()}`);
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.delete<Record<string, never>, DeleteScenariosResponse>(url, {}, headers);
}

/**
 * Suggest values for scenario fields (scenario_name, tags, user_id) with optional prefix
 * and filter context. Scoped to the current query state so suggestions narrow
 * as filters are applied.
 */
export async function suggestScenarioFields(
  accessToken: string,
  field: 'scenario_name' | 'tags' | 'user_id' | 'suite' | 'suite_run',
  prefix: string = '',
  context: ScenarioSuggestContext = {},
  limit: number = 20
): Promise<{ status: number; data: ScenarioSuggestResponse | null }> {
  const params = new URLSearchParams({ field, limit: limit.toString() });
  if (prefix) params.append('prefix', prefix);
  const url = buildUrl(`/scenarios/suggest?${params.toString()}`);
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<ScenarioSuggestContext, ScenarioSuggestResponse>(url, context, headers);
}

/**
 * Mint service-specific JWT tokens for workspace services
 * Exchanges the Auth0 access token for Rome-signed tokens with service-specific audiences
 * @param accessToken - The Auth0 access token (sessions.antioch.com audience)
 * @returns Promise with service tokens for workspace, streaming, telemetry
 */
export async function mintServiceTokens(accessToken: string): Promise<{ status: number; data: ServiceTokens | null }> {
  const url = buildUrl('/token/mint');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  const response = await Http.post<Record<string, never>, MintTokensResponse>(url, {}, headers);

  if (response.status === 200 && response.data) {
    return { status: response.status, data: response.data.data };
  }

  return { status: response.status, data: null };
}

/**
 * List all SSH keys for the authenticated user
 * @param accessToken - The Auth0 access token
 * @returns Promise with the SSH keys list response
 */
export async function listSSHKeys(accessToken: string): Promise<{ status: number; data: SSHKeysListResponse | null }> {
  const url = buildUrl('/ssh/keys');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<SSHKeysListResponse>(url, headers);
}

/**
 * Add a new SSH key for the authenticated user
 * @param accessToken - The Auth0 access token
 * @param name - User-assigned name for the key
 * @param publicKey - The SSH public key content
 * @returns Promise with the created SSH key
 */
export async function addSSHKey(
  accessToken: string,
  name: string,
  publicKey: string
): Promise<{ status: number; data: SSHKeyResponse | null }> {
  const url = buildUrl('/ssh/keys');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<{ name: string; public_key: string }, SSHKeyResponse>(url, { name, public_key: publicKey }, headers);
}

/**
 * Update an existing SSH key for the authenticated user
 * @param accessToken - The Auth0 access token
 * @param keyId - The SSH key identifier
 * @param name - New name for the key (optional)
 * @param publicKey - New public key content (optional)
 * @returns Promise with the updated SSH key
 */
export async function updateSSHKey(
  accessToken: string,
  keyId: string,
  name?: string,
  publicKey?: string
): Promise<{ status: number; data: SSHKeyResponse | null }> {
  const url = buildUrl(`/ssh/keys/${keyId}`);
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  const body: { name?: string; public_key?: string } = {};
  if (name !== undefined) body.name = name;
  if (publicKey !== undefined) body.public_key = publicKey;

  return Http.put<typeof body, SSHKeyResponse>(url, body, headers);
}

/**
 * Delete an SSH key for the authenticated user
 * @param accessToken - The Auth0 access token
 * @param keyId - The SSH key identifier
 * @returns Promise with the response
 */
export async function deleteSSHKey(
  accessToken: string,
  keyId: string
): Promise<{ status: number; data: { message: string; status_code: number } | null }> {
  const url = buildUrl(`/ssh/keys/${keyId}`);
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.delete<Record<string, never>, { message: string; status_code: number }>(url, {}, headers);
}

/**
 * Get workspace usage statistics for the authenticated user's organization
 * @param accessToken - The Auth0 access token
 * @param startTime - Unix timestamp for start of query range
 * @param endTime - Unix timestamp for end of query range
 * @returns Promise with the usage statistics response
 */
export async function getWorkspaceUsage(
  accessToken: string,
  startTime: number,
  endTime: number,
  options?: { includeSupport?: boolean }
): Promise<{ status: number; data: UsageResponse | null }> {
  const params = new URLSearchParams({
    start_time: String(startTime),
    end_time: String(endTime),
  });
  if (options?.includeSupport) {
    params.set('include_support', 'true');
  }
  const url = buildUrl(`/workspace/usage?${params.toString()}`);
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<UsageResponse>(url, headers);
}

/**
 * Get workspace limits for the authenticated user's organization
 * @param accessToken - The Auth0 access token
 * @returns Promise with the workspace limits response
 */
export async function getWorkspaceLimits(
  accessToken: string
): Promise<{ status: number; data: WorkspaceLimitsResponse | null }> {
  const url = buildUrl('/workspace/limits');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<WorkspaceLimitsResponse>(url, headers);
}

/**
 * List all members of the authenticated user's organization
 * @param accessToken - The Auth0 access token
 * @returns Promise with the organization members response
 */
export async function listOrgMembers(
  accessToken: string
): Promise<{ status: number; data: OrgMembersResponse | null }> {
  const url = buildUrl('/org/members');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<OrgMembersResponse>(url, headers);
}

/**
 * Get the current user's information and role in the organization
 * @param accessToken - The Auth0 access token
 * @returns Promise with the current user response
 */
export async function getCurrentUser(
  accessToken: string
): Promise<{ status: number; data: CurrentUserResponse | null }> {
  const url = buildUrl('/org/me');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<CurrentUserResponse>(url, headers);
}

/**
 * Invite a new member to the organization
 * @param accessToken - The Auth0 access token
 * @param email - Email address to invite
 * @param role - Role to assign to the invited member
 * @returns Promise with the invite response
 */
export async function inviteMember(
  accessToken: string,
  email: string,
  role: OrgRole
): Promise<{ status: number; data: InviteMemberResponse | null }> {
  const url = buildUrl('/org/members/invite');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<{ email: string; role: OrgRole }, InviteMemberResponse>(url, { email, role }, headers);
}

/**
 * Remove a member from the organization
 * @param accessToken - The Auth0 access token
 * @param userId - The Auth0 user ID to remove
 * @returns Promise with the remove response
 */
export async function removeMember(
  accessToken: string,
  userId: string
): Promise<{ status: number; data: RemoveMemberResponse | null }> {
  const url = buildUrl('/org/members/remove');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<{ user_id: string }, RemoveMemberResponse>(url, { user_id: userId }, headers);
}

/**
 * Update a member's role in the organization
 * @param accessToken - The Auth0 access token
 * @param userId - The Auth0 user ID to update
 * @param role - The new role to assign
 * @returns Promise with the update response
 */
export async function updateMemberRole(
  accessToken: string,
  userId: string,
  role: OrgRole
): Promise<{ status: number; data: UpdateRoleResponse | null }> {
  const url = buildUrl('/org/members/role');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<{ user_id: string; role: OrgRole }, UpdateRoleResponse>(url, { user_id: userId, role }, headers);
}

/**
 * List pending invitations for the organization
 * @param accessToken - The Auth0 access token
 * @returns Promise with the pending invitations response
 */
export async function listPendingInvites(
  accessToken: string
): Promise<{ status: number; data: PendingInvitesResponse | null }> {
  const url = buildUrl('/org/invitations');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.get<PendingInvitesResponse>(url, headers);
}

/**
 * Revoke a pending invitation
 * @param accessToken - The Auth0 access token
 * @param invitationId - The invitation ID to revoke
 * @returns Promise with the revoke response
 */
export async function revokeInvite(
  accessToken: string,
  invitationId: string
): Promise<{ status: number; data: RevokeInviteResponse | null }> {
  const url = buildUrl('/org/invitations/revoke');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  return Http.post<{ invitation_id: string }, RevokeInviteResponse>(url, { invitation_id: invitationId }, headers);
}
